<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { AnyRepurchasedContract } from '../../../../Types/restructuring'
import ButtonAll from '../UI/Button/ButtonAll.vue'
import { RepurchasedContractType } from '../../../../Constants/RepurchasedContractType.ts'
import { Routes } from '../../../../Constants/Routes.ts'
import PageLink from '../UI/PageLink.vue'

const props = withDefaults(
  defineProps<{
    contract: AnyRepurchasedContract
    readonly?: boolean
  }>(),
  { readonly: false },
)

const emit = defineEmits<{
  edit: [{ contract: AnyRepurchasedContract }]
  delete: [{ contract: AnyRepurchasedContract }]
}>()

const { t, n, d } = useI18n()

const refinancerName = computed(
  () =>
    props.contract.refinancer?.name ?? t('restructuring.fields.outstandingRepurchase.contract.refinancer.placeholder'),
)
const repurchaseDate = computed(() =>
  props.contract.repurchaseDate !== null ? d(props.contract.repurchaseDate, 'date') : '-',
)

const repurchaseAmount = computed(() => n(Number(props.contract.repurchaseAmount), 'currency'))

function edit() {
  emit('edit', { contract: props.contract })
}

function remove() {
  emit('delete', { contract: props.contract })
}
</script>

<template>
  <tr>
    <td class="font-weight-bold" data-test="repurchased-contract-number">
      <template v-if="contract.type === RepurchasedContractType.default">
        <PageLink :to="{ name: Routes.WALLET_CONTRACT_VIEW, params: { id: contract.originalContractId } }" external>
          {{ contract.number ?? '-' }}
        </PageLink>
      </template>
      <template v-else>
        {{ contract.number ?? '-' }}
      </template>
    </td>
    <td data-test="repurchased-contract-client">
      <template v-if="contract.type === RepurchasedContractType.default">
        <PageLink
          :to="{ name: Routes.ENTITY_VIEW, params: { id: contract.client?.id, slug: contract.client?.name } }"
          external
        >
          {{ contract.client?.name ?? '-' }}
        </PageLink>
      </template>
      <template v-else>
        {{ contract.client ?? '-' }}
      </template>
    </td>
    <td data-test="repurchased-contract-repurchase-date">
      {{ repurchaseDate }}
    </td>
    <td data-test="repurchased-contract-refinancer">
      {{ refinancerName }}
    </td>
    <td data-test="repurchased-contract-agreement-number">
      {{ contract.agreementNumber ?? '-' }}
    </td>
    <td data-test="repurchased-contract-repurchase-amount">
      {{ repurchaseAmount }}
    </td>
    <td v-if="!readonly" class="col-1">
      <ButtonAll
        type="action"
        icon="modify"
        size="small"
        margin="no"
        data-test="repurchased-contract-edit-button"
        @click="edit"
      />
      <ButtonAll
        type="danger"
        icon="trash"
        size="small"
        margin="no"
        data-test="repurchased-contract-delete-button"
        class="ml-2"
        @click="remove"
      />
    </td>
  </tr>
</template>

<style scoped></style>
